/**
 * Hotel Booking Review Slice
 *
 * Manages hotel booking review state including:
 * - Review data loading states
 * - Policy rules data (from API)
 * - Travellers (backend-controlled - NEVER derive locally)
 * - Country codes data (from API)
 * - Fare summary (updated by API responses)
 * - Coupon validation (loading/error only, updates fareSummary)
 *
 * STATE MANAGEMENT RULE:
 * - Traveller state must be fully backend-controlled
 * - Frontend should never derive or compute traveller selection state
 * - Always override state with API response
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  HotelRulesData,
  CountryCodesData,
  HotelSavedTraveller,
  HotelFareSummary,
  HotelCouponUpdateTemplateData,
  HotelTravellerDetailTemplateData,
  HotelCheckoutTemplateData,
  HotelGstDetailTemplateData,
  HotelPanDetailTemplateData,
  GstDetailRequest,
} from '@contracts';
import { logger } from '@/shared/utils/logger';
import { updateTemplateFromStream } from '@/features/chat/redux/slices/templateSlice';
import {
  fetchHotelBookingReviewThunk,
  fetchHotelPolicyRulesThunk,
  fetchHotelCountryCodesThunk,
  validateCouponThunk,
  updatePriceThunk,
  addTravellerThunk,
  editTravellerThunk,
  toggleTravellerSelectionThunk,
  addGstThunk,
  editGstThunk,
  removeGstThunk,
  addPanThunk,
  removePanThunk,
} from '../thunks/booking-review';

// ============================================
// State Types
// ============================================

export interface ReviewError {
  message: string;
  code?: string;
}

export interface HotelBookingReviewState {
  // Review
  isReviewLoading: boolean;
  reviewError: ReviewError | null;
  // Policy Rules
  isPolicyRulesLoading: boolean;
  policyRulesError: string | null;
  policyRulesData: HotelRulesData | null;
  // Travellers (backend-controlled - NEVER derive locally)
  // Frontend should always use API response, never compute selection state
  travellers: HotelSavedTraveller[];
  isTravellerOperationLoading: boolean;
  travellerOperationError: string | null;
  // Country Codes
  isCountryCodesLoading: boolean;
  countryCodesError: string | null;
  countryCodesData: CountryCodesData | null;
  // Fare Summary (updated by booking-review, coupon, and update-price APIs)
  fareSummary: HotelFareSummary;
  // Original fare summary from booking-review (used to revert on error when no TCS)
  originalFareSummary: HotelFareSummary;
  // TCS fare summary from update-price API (used as fallback when coupon fails with TCS applied)
  tcsFareSummary: HotelFareSummary | null;
  // Coupon - single source of truth for applied coupon (updated by thunk and chat stream)
  appliedCouponCode: string | null;
  isCouponValidating: boolean;
  couponError: string | null;
  // Update Price (PAN/TCS)
  isUpdatePriceLoading: boolean;
  updatePriceError: string | null;
  // GST Details (backend-controlled - NEVER derive locally)
  // Frontend should always use API response
  savedGstDetails: GstDetailRequest | null;
  isGstSavedToBackend: boolean;
  isGstOperationLoading: boolean;
  gstOperationError: string | null;
  // PAN Details (backend-controlled)
  isPanOperationLoading: boolean;
  panOperationError: string | null;
  /** Current PAN number confirmed by the API (used at checkout) */
  currentPanNumber: string;
  /** PAN number received from chat stream — used to pre-fill PanCardV2 */
  panFromChat: string | null;
  /** Whether the PAN from chat was validated by the Agent */
  isPanFromChatValidated: boolean;
  /** Checkout URL received from chat stream — stored for consent popup before redirect */
  chatCheckoutUrl: string | null;
}

// ============================================
// Initial State
// ============================================

export const initialHotelBookingReviewState: HotelBookingReviewState = {
  isReviewLoading: false,
  reviewError: null,
  isPolicyRulesLoading: false,
  policyRulesError: null,
  policyRulesData: null,
  travellers: [],
  isTravellerOperationLoading: false,
  travellerOperationError: null,
  isCountryCodesLoading: false,
  countryCodesError: null,
  countryCodesData: null,
  fareSummary: {
    reviewPrice: null,
    fareBreakup: null,
  },
  originalFareSummary: {
    reviewPrice: null,
    fareBreakup: null,
  },
  tcsFareSummary: null,
  appliedCouponCode: null,
  isCouponValidating: false,
  couponError: null,
  isUpdatePriceLoading: false,
  updatePriceError: null,
  savedGstDetails: null,
  isGstSavedToBackend: false,
  isGstOperationLoading: false,
  gstOperationError: null,
  isPanOperationLoading: false,
  panOperationError: null,
  currentPanNumber: '',
  panFromChat: null,
  isPanFromChatValidated: false,
  chatCheckoutUrl: null,
};

// ============================================
// Helper Functions
// ============================================

/**
 * Normalize traveller data with defaults for missing fields.
 * - Primary traveller: salutation defaults to "Mr"
 * - Primary traveller: countryCode defaults to "+91" (format matches dropdown options)
 */
function normalizeTraveller(traveller: HotelSavedTraveller): HotelSavedTraveller {
  if (traveller.isPrimary) {
    return {
      ...traveller,
      salutation: traveller.salutation ?? 'Mr',
      countryCode: traveller.countryCode ?? '+91',
    };
  }
  return traveller;
}

/**
 * Normalize all travellers in the list
 */
function normalizeTravellers(travellers: HotelSavedTraveller[]): HotelSavedTraveller[] {
  return travellers.map(normalizeTraveller);
}

// ============================================
// Slice
// ============================================

const bookingReviewSlice = createSlice({
  name: 'hotels/bookingReview',
  initialState: initialHotelBookingReviewState,
  reducers: {
    setHotelFareSummary: (state, action: PayloadAction<HotelFareSummary>) => {
      state.fareSummary = action.payload;
    },
    /** Set original fare summary from booking-review props (for coupon error revert) */
    setOriginalFareSummary: (state, action: PayloadAction<HotelFareSummary>) => {
      state.originalFareSummary = action.payload;
    },
    clearPolicyRules: (state) => {
      state.policyRulesData = null;
      state.policyRulesError = null;
    },
    /**
     * Set travellers from template data (initial load from Avail-Room response).
     * This is the only way to set initial travellers - backend is source of truth.
     * NEVER compute or derive traveller state locally.
     * Applies defaults: primary traveller salutation -> "Mr", countryCode -> "91"
     */
    setTravellersFromTemplate: (state, action: PayloadAction<HotelSavedTraveller[]>) => {
      state.travellers = normalizeTravellers(action.payload);
      state.travellerOperationError = null;
    },
    /**
     * Update coupon state from chat stream response.
     * Called when chat dispatches hotels-validate-coupon template update.
     * This ensures both click and chat flows update the same state.
     */
    updateCouponFromChat: (
      state,
      action: PayloadAction<{
        appliedCouponCode: string | null;
        priceSummary: {
          reviewPrice: HotelFareSummary['reviewPrice'];
          fareBreakup: HotelFareSummary['fareBreakup'];
        } | null;
      }>,
    ) => {
      state.appliedCouponCode = action.payload.appliedCouponCode;
      if (action.payload.priceSummary) {
        state.fareSummary = {
          reviewPrice: action.payload.priceSummary.reviewPrice,
          fareBreakup: action.payload.priceSummary.fareBreakup,
        };
      }
    },
    /** Clear review error (after popup is dismissed) */
    clearReviewError: (state) => {
      state.reviewError = null;
    },
    resetHotelBookingReviewState: () => initialHotelBookingReviewState,
    /**
     * Set GST details from template data (initial load from Avail-Room response).
     * This is the only way to set initial GST - backend is source of truth.
     */
    setGstFromTemplate: (state, action: PayloadAction<GstDetailRequest | null>) => {
      state.savedGstDetails = action.payload;
      state.isGstSavedToBackend = action.payload !== null;
      state.gstOperationError = null;
    },
    /**
     * Clear GST operation error
     */
    clearGstOperationError: (state) => {
      state.gstOperationError = null;
    },
    /**
     * Clear PAN from chat after it has been consumed by PanCardV2.
     * Called once the PAN value is applied to the component so we don't re-apply.
     */
    clearPanFromChat: (state) => {
      state.panFromChat = null;
      state.isPanFromChatValidated = false;
    },
    clearChatCheckoutUrl: (state) => {
      state.chatCheckoutUrl = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Hotel Booking Review
    builder
      .addCase(fetchHotelBookingReviewThunk.pending, (state) => {
        state.isReviewLoading = true;
        state.reviewError = null;
      })
      .addCase(fetchHotelBookingReviewThunk.fulfilled, (state, action) => {
        state.isReviewLoading = false;
        const { priceSummary } = action.payload.templateData;
        const fareSummaryData = {
          reviewPrice: priceSummary.reviewPrice,
          fareBreakup: priceSummary.fareBreakup,
        };
        state.fareSummary = fareSummaryData;
        // Store original price from booking-review to revert on coupon error
        state.originalFareSummary = fareSummaryData;
      })
      .addCase(fetchHotelBookingReviewThunk.rejected, (state, action) => {
        state.isReviewLoading = false;
        if (typeof action.payload === 'string') {
          state.reviewError = { message: action.payload };
        } else if (action.payload) {
          state.reviewError = { message: action.payload.message, code: action.payload.code ?? '' };
        } else {
          state.reviewError = { message: 'Failed to fetch hotel booking review' };
        }
      });

    // Fetch Hotel Policy Rules
    builder
      .addCase(fetchHotelPolicyRulesThunk.pending, (state) => {
        state.isPolicyRulesLoading = true;
        state.policyRulesError = null;
      })
      .addCase(fetchHotelPolicyRulesThunk.fulfilled, (state, action) => {
        state.isPolicyRulesLoading = false;
        state.policyRulesData = action.payload.rulesData;
      })
      .addCase(fetchHotelPolicyRulesThunk.rejected, (state, action) => {
        state.isPolicyRulesLoading = false;
        state.policyRulesError = action.payload ?? 'Failed to fetch hotel policy rules';
      });

    // Traveller Operations (add/edit/select/deselect) - Backend-controlled
    // All operations return the complete traveller list - ALWAYS override state
    builder
      .addCase(addTravellerThunk.pending, (state) => {
        state.isTravellerOperationLoading = true;
        state.travellerOperationError = null;
      })
      .addCase(addTravellerThunk.fulfilled, (state, action) => {
        state.isTravellerOperationLoading = false;
        // CRITICAL: Always override with backend response, apply defaults
        state.travellers = normalizeTravellers(action.payload.travellers);
      })
      .addCase(addTravellerThunk.rejected, (state, action) => {
        state.isTravellerOperationLoading = false;
        state.travellerOperationError = action.payload ?? 'Failed to add traveller';
      });

    builder
      .addCase(editTravellerThunk.pending, (state) => {
        state.isTravellerOperationLoading = true;
        state.travellerOperationError = null;
      })
      .addCase(editTravellerThunk.fulfilled, (state, action) => {
        state.isTravellerOperationLoading = false;
        // CRITICAL: Always override with backend response, apply defaults
        state.travellers = normalizeTravellers(action.payload.travellers);
      })
      .addCase(editTravellerThunk.rejected, (state, action) => {
        state.isTravellerOperationLoading = false;
        state.travellerOperationError = action.payload ?? 'Failed to edit traveller';
      });

    builder
      .addCase(toggleTravellerSelectionThunk.pending, (state) => {
        state.isTravellerOperationLoading = true;
        state.travellerOperationError = null;
      })
      .addCase(toggleTravellerSelectionThunk.fulfilled, (state, action) => {
        state.isTravellerOperationLoading = false;
        // CRITICAL: Always override with backend response, apply defaults
        state.travellers = normalizeTravellers(action.payload.travellers);
      })
      .addCase(toggleTravellerSelectionThunk.rejected, (state, action) => {
        state.isTravellerOperationLoading = false;
        state.travellerOperationError = action.payload ?? 'Failed to update traveller selection';
      });

    // Fetch Country Codes
    builder
      .addCase(fetchHotelCountryCodesThunk.pending, (state) => {
        state.isCountryCodesLoading = true;
        state.countryCodesError = null;
      })
      .addCase(fetchHotelCountryCodesThunk.fulfilled, (state, action) => {
        state.isCountryCodesLoading = false;
        state.countryCodesData = action.payload.countryCodesData;
      })
      .addCase(fetchHotelCountryCodesThunk.rejected, (state, action) => {
        state.isCountryCodesLoading = false;
        state.countryCodesError = action.payload ?? 'Failed to fetch country codes';
      });

    // Validate Coupon (just loading/error + update fareSummary)
    builder
      .addCase(validateCouponThunk.pending, (state) => {
        state.isCouponValidating = true;
        state.couponError = null;
      })
      .addCase(validateCouponThunk.fulfilled, (state, action) => {
        state.isCouponValidating = false;
        // Update fare summary with new pricing
        if (action.payload.priceSummary) {
          state.fareSummary = {
            reviewPrice: action.payload.priceSummary.reviewPrice,
            fareBreakup: action.payload.priceSummary.fareBreakup,
          };
        }
        // Update applied coupon code from API response (single source of truth)
        state.appliedCouponCode = action.payload.appliedCouponCode ?? null;
      })
      .addCase(validateCouponThunk.rejected, (state, action) => {
        state.isCouponValidating = false;
        state.couponError = action.payload ?? 'Failed to validate coupon';
        // Clear applied coupon on failure
        state.appliedCouponCode = null;
        // On coupon failure: use TCS price if available, otherwise revert to original
        if (state.tcsFareSummary?.reviewPrice) {
          // TCS is applied - use TCS price (from update-price API)
          state.fareSummary = state.tcsFareSummary;
        } else if (state.originalFareSummary.reviewPrice) {
          // No TCS - revert to original booking-review price
          state.fareSummary = state.originalFareSummary;
        }
      });

    // Update Price (PAN/TCS - just loading/error + update fareSummary)
    builder
      .addCase(updatePriceThunk.pending, (state) => {
        state.isUpdatePriceLoading = true;
        state.updatePriceError = null;
      })
      .addCase(updatePriceThunk.fulfilled, (state, action) => {
        state.isUpdatePriceLoading = false;
        // Update fare summary with new pricing from update-price API
        if (action.payload.priceSummary) {
          const tcsPriceData = {
            reviewPrice: action.payload.priceSummary.reviewPrice,
            fareBreakup: action.payload.priceSummary.fareBreakup,
          };
          state.fareSummary = tcsPriceData;
          // Store TCS price for fallback when coupon fails
          state.tcsFareSummary = tcsPriceData;
        }
      })
      .addCase(updatePriceThunk.rejected, (state, action) => {
        state.isUpdatePriceLoading = false;
        state.updatePriceError = action.payload ?? 'Failed to update price';
        state.tcsFareSummary = null;
        if (state.originalFareSummary.reviewPrice) {
          state.fareSummary = state.originalFareSummary;
        }
      });

    // GST Operations (add/edit/remove) - Backend-controlled
    // All operations return the updated GST details - ALWAYS override state
    builder
      .addCase(addGstThunk.pending, (state) => {
        state.isGstOperationLoading = true;
        state.gstOperationError = null;
      })
      .addCase(addGstThunk.fulfilled, (state, action) => {
        state.isGstOperationLoading = false;
        // CRITICAL: Always override with backend response
        state.savedGstDetails = action.payload.gstDetails;
        // Use isGstApplied from API response (don't derive from gstDetails)
        state.isGstSavedToBackend = action.payload.isGstApplied;
      })
      .addCase(addGstThunk.rejected, (state, action) => {
        state.isGstOperationLoading = false;
        state.gstOperationError = action.payload ?? 'Failed to add GST details';
      });

    builder
      .addCase(editGstThunk.pending, (state) => {
        state.isGstOperationLoading = true;
        state.gstOperationError = null;
      })
      .addCase(editGstThunk.fulfilled, (state, action) => {
        state.isGstOperationLoading = false;
        // CRITICAL: Always override with backend response
        state.savedGstDetails = action.payload.gstDetails;
        // Use isGstApplied from API response (don't derive from gstDetails)
        state.isGstSavedToBackend = action.payload.isGstApplied;
      })
      .addCase(editGstThunk.rejected, (state, action) => {
        state.isGstOperationLoading = false;
        state.gstOperationError = action.payload ?? 'Failed to edit GST details';
      });

    builder
      .addCase(removeGstThunk.pending, (state) => {
        state.isGstOperationLoading = true;
        state.gstOperationError = null;
      })
      .addCase(removeGstThunk.fulfilled, (state, action) => {
        state.isGstOperationLoading = false;
        // CRITICAL: Clear GST details on remove
        state.savedGstDetails = action.payload.gstDetails;
        // Use isGstApplied from API response (don't derive from gstDetails)
        state.isGstSavedToBackend = action.payload.isGstApplied;
      })
      .addCase(removeGstThunk.rejected, (state, action) => {
        state.isGstOperationLoading = false;
        state.gstOperationError = action.payload ?? 'Failed to remove GST details';
      });

    // PAN Operations (add/remove) - Backend-controlled
    builder
      .addCase(addPanThunk.pending, (state) => {
        state.isPanOperationLoading = true;
        state.panOperationError = null;
      })
      .addCase(addPanThunk.fulfilled, (state, action) => {
        state.isPanOperationLoading = false;
        state.currentPanNumber = action.payload.panNumber ?? '';
      })
      .addCase(addPanThunk.rejected, (state, action) => {
        state.isPanOperationLoading = false;
        state.panOperationError = action.payload ?? 'Failed to add PAN details';
      });

    builder
      .addCase(removePanThunk.pending, (state) => {
        state.isPanOperationLoading = true;
        state.panOperationError = null;
      })
      .addCase(removePanThunk.fulfilled, (state) => {
        state.isPanOperationLoading = false;
        state.currentPanNumber = '';
      })
      .addCase(removePanThunk.rejected, (state, action) => {
        state.isPanOperationLoading = false;
        state.panOperationError = action.payload ?? 'Failed to remove PAN details';
      });

    // Live streaming: merge coupon/pricing data when CB streams a coupon-update response.
    // This is an action-update template — templateSlice skips the panel switch so the
    // review screen stays visible while pricing updates in place.
    builder.addCase(updateTemplateFromStream, (state, action) => {
      const { activeTemplate, templateData } = action.payload;

      if (!templateData) return;

      // Handle coupon updates from chat stream
      if (activeTemplate === 'hotels-coupon-update') {
        const couponData = templateData as HotelCouponUpdateTemplateData;
        state.appliedCouponCode = couponData.appliedCouponCode ?? null;
        if (couponData.priceSummary) {
          state.fareSummary = {
            reviewPrice: couponData.priceSummary.reviewPrice,
            fareBreakup: couponData.priceSummary.fareBreakup,
          };
        }
        return;
      }

      // Handle traveller detail updates from chat stream
      // This covers add, edit, select, and deselect operations performed via chat
      if (activeTemplate === 'hotels-traveller-detail') {
        const travellerData = templateData as HotelTravellerDetailTemplateData;
        if (travellerData.travellers) {
          // CRITICAL: Always override with backend response, apply defaults
          state.travellers = normalizeTravellers(travellerData.travellers);
        }
        // Clear any previous operation error on successful update
        state.travellerOperationError = null;
        return;
      }

      // Handle checkout from chat stream
      // Store the URL so the UI can show consent popup before redirecting
      if (activeTemplate === 'hotels-checkout') {
        const checkoutData = templateData as HotelCheckoutTemplateData;
        if (checkoutData.checkoutUrl) {
          logger.info('[bookingReviewSlice] Chat checkout URL received:', checkoutData.checkoutUrl);
          state.chatCheckoutUrl = checkoutData.checkoutUrl;
        } else if (checkoutData.error) {
          logger.error('[bookingReviewSlice] Checkout error from chat:', checkoutData.error);
        }
        return;
      }

      // Handle hotels-gst-detail template from chat stream
      // CRITICAL: Backend is the single source of truth for GST state
      // Response structure: resources[0].data.gstDetails and resources[0].data.isGstApplied
      if (activeTemplate === 'hotels-gst-detail') {
        const gstData = templateData as HotelGstDetailTemplateData;
        // Always override frontend state with backend response
        state.savedGstDetails = gstData.gstDetails ?? null;
        // Use isGstApplied from API response (don't derive from gstDetails)
        state.isGstSavedToBackend = gstData.isGstApplied;
        state.isGstOperationLoading = false;
        // Handle errors from stream
        if (gstData.error) {
          state.gstOperationError = gstData.error.message;
          logger.error('[bookingReviewSlice] GST operation error from chat:', gstData.error);
        } else {
          state.gstOperationError = null;
        }
        return;
      }

      // Handle hotels-pan-detail template from chat stream
      // When Agent sends PAN via chat, store it so the UI can pre-fill PanCardV2
      // and call the CBackend PAN API with is_validated based on frontend validation
      if (activeTemplate === 'hotels-pan-detail') {
        const panData = templateData as HotelPanDetailTemplateData;
        if (panData.error) {
          state.panOperationError = panData.error.message;
          logger.error('[bookingReviewSlice] PAN operation error from chat:', panData.error);
        } else {
          state.panFromChat = panData.panNumber ?? '';
          state.isPanFromChatValidated = panData.isValidated;
          state.panOperationError = null;
          state.isPanOperationLoading = false;
        }
        return;
      }
    });
  },
});

// ============================================
// Exports
// ============================================

export const {
  setHotelFareSummary,
  setOriginalFareSummary,
  clearPolicyRules,
  setTravellersFromTemplate,
  updateCouponFromChat,
  clearReviewError,
  resetHotelBookingReviewState,
  setGstFromTemplate,
  clearGstOperationError,
  clearPanFromChat,
  clearChatCheckoutUrl,
} = bookingReviewSlice.actions;
export const bookingReviewReducer = bookingReviewSlice.reducer;
